import React, { PropTypes, Component } from 'react';

import { NitroHeading, NitroBadge, NitroSearchBar, NitroCheckbox, NitroGrid, NitroIcon } from 'nitro-components';

import { searchReports } from '../../../helper';
import Disabled from '../../../../../components/libs/disabled';

import { remove, intersection, union, difference, findIndex } from 'lodash';

import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import styled from 'styled-components';

const PanelHeader = styled.div`
  line-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Panel = styled.div`
  background: ${props => props.theme.color.white};
  margin: 10px 0;
  border: 1px solid ${props => props.theme.color.border};
  border-radius: 2px;
  overflow: hidden;
`;
const PanelListHeader = styled.div`
  padding: 8px 16px;
  background-color: ${props => props.theme.color[props.variant]};
`;
PanelListHeader.defaultProps = {
  variant: 'base2'
};

const ScheduleEditPanelList = styled.ul`
  margin: 0;
  padding: 0 16px;
  height: 500px;
  overflow-y: auto;
`;

const ScheduleEdit = styled.li`
  z-index: 100;
  list-style: none;
  position: relative;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.color.border};
`;

const DragIcon = styled.div`
  position: absolute;
  right: 0;
  top: 8px;
  line-height: 24px;
  z-index: 100;
  cursor: row-resize;
`;

const DisableContainer = styled.div`
  opacity: ${props => props.disabled ? 0.5: 1};
`;

const DisableCover = styled(Disabled)`
  z-index: 100;
`;

const getVariant = type => (type === 'available' ? 'secondary' : 'primary');

const DragHandle = SortableHandle(() =>
  <DragIcon>
    <NitroIcon name="drag-handler" size={16} />
  </DragIcon>
);

const FieldItem = SortableElement(({ field, checked, type, checkField, fixed }) => {
  let dragHandle;

  if (type === 'selected' && !fixed) {
    dragHandle = <DragHandle />;
  }

  return (
    <ScheduleEdit>
      <NitroCheckbox
        size={18}
        variant={getVariant(type)}
        label={field.name}
        checked={checked}
        action={() => checkField(field)}
      />
      {dragHandle}
    </ScheduleEdit>
  );
});

const FieldsList = SortableContainer(({ fields, check, checkField, type, fixed, variant, allChecked, checkAll }) => {
  return (
    <ScheduleEditPanelList>
      <ScheduleEdit>
        <NitroCheckbox
          size={20}
          variant={variant}
          labelVariant={variant}
          label="Select all"
          tickVariant="white"
          checked={allChecked}
          action={checkAll}
        />
      </ScheduleEdit>
      {fields.map((field, index) => {
        const checked = check(field);
        return (
          <FieldItem
            fixed={fixed}
            key={`field-${index}`}
            index={index}
            field={field}
            checked={checked}
            type={type}
            checkField={checkField}
          />
        );
      })}
    </ScheduleEditPanelList>
  );
});

class FieldSelection extends Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      search: '',
      selectedFields: [],
      fields: this.props.fields
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.fields.length !== this.props.fields.length) {
      this.setState({
        fields: nextProps.fields
      });
    }
  }

  get allSelected() {
    const { selectedFields, fields } = this.state;

    // No fields, no selection
    if (fields.length === 0) {
      return false;
    }

    if (selectedFields.length > 0) {
      return intersection(fields, selectedFields).length === fields.length;
    }

    return false;
  }

  search = text => {
    const fields = [...this.props.fields];
    this.setState({
      search: text,
      fields: searchReports(text, fields)
    });
  };

  checkAll = () => {
    const { selectedFields, fields } = this.state;
    if (this.allSelected) {
      // Do differenciate with selected and all
      this.updateSelected(difference(selectedFields, fields));
    } else {
      this.updateSelected(union(selectedFields, fields));
    }
  };

  checkField = field => {
    // Copy selected fields
    let selectedFields = [...this.state.selectedFields];
    // Remove current if checked
    if (this.getFieldChecked(field)) {
      remove(selectedFields, f => f.id === field.id);
      this.updateSelected(selectedFields);
    } else {
      this.updateSelected([...selectedFields, field]);
    }
  };

  getFieldChecked = field => {
    return findIndex(this.state.selectedFields, { id: field.id }) !== -1;
  };

  updateSelected = fields => {
    this.setState({
      selectedFields: fields
    });
    this.props.action(fields);
  };

  reset = () => {
    this.searchBar.reset();
    this.updateSelected([]);
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const fields = arrayMove(this.state.fields, oldIndex, newIndex);
    this.setState({ fields });
    this.props.arrange(fields);
  };

  render() {
    const { type, disabled, header, fields } = this.props;

    const variant = getVariant(type);

    return (
      <NitroGrid grid={2}>
        <DisableContainer disabled={disabled}>
          <PanelHeader>
            <NitroHeading inline variant={variant} label={`${header}`} size="3" noMargin />
            <NitroBadge variant={variant} label={fields.length} size={10} />
          </PanelHeader>

          {/* <NitroSearchBar
            ref={ref => (this.searchBar = ref)}
            action={this.search}
            float="right"
            hintText={`Search ${header.toLowerCase()}`}
          /> */}

          <Panel>
            <PanelListHeader variant={variant}>
              <NitroSearchBar
                block
                variant="white"
                ref={ref => (this.searchBar = ref)}
                action={this.search}
                hintText={`Search ${header.toLowerCase()}`}
              />
            </PanelListHeader>
            <FieldsList
              variant={variant}
              allChecked={this.allSelected}
              checkAll={this.checkAll}
              fixed={this.state.search.length > 0}
              useDragHandle
              type={type}
              fields={this.state.fields}
              check={this.getFieldChecked}
              checkField={this.checkField}
              onSortEnd={this.onSortEnd}
            />
          </Panel>
          {disabled && <DisableCover transparent />}
        </DisableContainer>
      </NitroGrid>
    );
  }
}

FieldSelection.propTypes = {
  disabled: PropTypes.bool,
  header: PropTypes.string,
  type: PropTypes.string,
  fields: PropTypes.array.isRequired,
  action: PropTypes.func.isRequired,
  arrange: PropTypes.func
};

export default FieldSelection;



// WEBPACK FOOTER //
// ./src/features/scheduling-reports/new-report/Fields/components/FieldSelection.js